// src/content.js
//
// All site copy in one place, so a change is one edit rather than a hunt
// through JSX.
//
// Copy is drawn from Brooke's existing maintenance page, so it is their own
// wording rather than a draft needing sign-off under clause 8.1.

export const SITE = {
  division: "Division 01",
  name: "Brooke Maintenance",
  tagline: "Service and maintenance of commercial refrigeration equipment",
  phone: "+27782205037",
  phoneLabel: "078 220 5037",
  gateway: "https://brookerefrigeration.co.za",
  siblings: [
    { label: "Cold Storage", href: "https://coldstorage.brookerefrigeration.com" },
    { label: "Requip", href: "https://requip.brookerefrigeration.com" },
  ],
  address: {
    street: "34 Joan Road, Winternest AH",
    locality: "Akasia",
    region: "Gauteng",
    postal: "0200",
  },
};

export const HERO = {
  heading: "Keep your business at the right temperature",
  body: "Over two decades of maintenance, servicing and repair on every type of commercial refrigeration unit. Preventative work that stops breakdowns, and emergency call-outs when one happens anyway.",
  stats: [
    { value: "2000", label: "Established" },
    { value: "850+", label: "Clients" },
    { value: "24/7", label: "Call-outs" },
  ],
};

// The service tag left on a unit after a visit. Reads as a record rather than
// a claim, which is the point of the section it sits in.
export const TAG = [
  { label: "Service", value: "Preventative + reactive" },
  { label: "Coverage", value: "Pretoria and surrounds" },
  { label: "Parts", value: "Genuine, fitted on site" },
  { label: "Record", value: "Logged against the unit" },
];

export const EQUIPMENT_SECTION = {
  eyebrow: "Services and maintenance",
  heading: "Commercial refrigeration maintenance",
  body: [
    "Brooke Refrigeration keeps your business running at the perfect temperature. With over two decades of experience, the team specialises in the maintenance, servicing and repair of all types of commercial refrigeration units.",
    "Every visit is logged against the equipment it was performed on, so the service history of a unit travels with it rather than living in a technician's memory.",
  ],
  items: [
    "Beverage coolers and upright freezers",
    "Underbars and make tables",
    "Ice machines",
    "Cold rooms and freezer rooms",
    "Display fridges and prep coolers",
  ],
};

export const WHY = {
  eyebrow: "Why Brooke",
  heading: "We do not just fix refrigeration, we preserve performance",
  body: "Whether it is a single cooler or a full cold room, Brooke Refrigeration keeps your equipment in top condition, so your business never loses its cool.",
  points: [
    {
      n: "01",
      title: "Preventative maintenance",
      body: "Scheduled work that reduces downtime, rather than waiting for the call.",
    },
    {
      n: "02",
      title: "Fast, professional repairs",
      body: "Qualified technicians, not a subcontracted crew learning your plant.",
    },
    {
      n: "03",
      title: "Genuine parts",
      body: "Correct components and quality workmanship, so the repair holds.",
    },
    {
      n: "04",
      title: "Emergency call-outs",
      body: "Available across Pretoria and the surrounding areas.",
    },
  ],
};

export const CONTRACTS = {
  eyebrow: "Maintenance contracts",
  heading: "Scheduled work beats an emergency",
  body: "A breakdown costs more than the repair. Stock write-offs, a closed counter and an overtime call-out add up to more than a year of scheduled visits. A contract sets the interval, prioritises your callouts and keeps a record of every job on every unit.",
  points: [
    "Visits at an agreed interval, set by how hard the plant works",
    "Priority response when something does go wrong",
    "A service history per unit, not per invoice",
    "Faults caught while they are still small",
  ],
};

export const AREAS = [
  "Pretoria",
  "Akasia",
  "Centurion",
  "Midrand",
  "Johannesburg",
  "Ekurhuleni",
];

// PLACEHOLDER DATA. These are not real jobs - they exist so the section can be
// built and reviewed. Replace every entry with an actual call-out, and set
// `image` to a file under public/gallery/, before this goes live. Publishing
// invented job records would misrepresent the business.
export const GALLERY = [
  {
    ref: "Example 01",
    sector: "Restaurant",
    location: "Pretoria",
    title: "Display fridge repair",
    body: "Placeholder entry. Replace with a real call-out, what was found and what was done.",
    image: "/gallery/job-01.jpg",
    alt: "",
    spec: [
      { label: "Type", value: "Reactive" },
      { label: "On site", value: "Same day" },
      { label: "Outcome", value: "Repaired" },
    ],
  },
  {
    ref: "Example 02",
    sector: "Butchery",
    location: "Akasia",
    title: "Cold room service",
    body: "Placeholder entry. Replace with a real call-out, what was found and what was done.",
    image: "/gallery/job-02.jpg",
    alt: "",
    spec: [
      { label: "Type", value: "Scheduled" },
      { label: "Interval", value: "Quarterly" },
      { label: "Outcome", value: "Passed" },
    ],
  },
  {
    ref: "Example 03",
    sector: "Bar",
    location: "Centurion",
    title: "Underbar and ice machine",
    body: "Placeholder entry. Replace with a real call-out, what was found and what was done.",
    image: "/gallery/job-03.jpg",
    alt: "",
    spec: [
      { label: "Type", value: "Reactive" },
      { label: "On site", value: "Next day" },
      { label: "Outcome", value: "Parts fitted" },
    ],
  },
  {
    ref: "Example 04",
    sector: "Retail",
    location: "Midrand",
    title: "Multideck plant service",
    body: "Placeholder entry. Replace with a real call-out, what was found and what was done.",
    image: "/gallery/job-04.jpg",
    alt: "",
    spec: [
      { label: "Type", value: "Scheduled" },
      { label: "Interval", value: "Monthly" },
      { label: "Outcome", value: "Passed" },
    ],
  },
];

// Feeds the subject_ref column on the enquiry, so the office knows what the
// enquiry is about without reading the message.
export const ENQUIRY_SUBJECTS = [
  { ref: "breakdown", label: "Something has broken down" },
  { ref: "contract", label: "Maintenance contract" },
  { ref: "service", label: "One-off service" },
  { ref: "quote", label: "Quote for a repair" },
  { ref: "other", label: "Something else" },
];